/* @flow */
import { observable } from 'mobx';
import * as React from 'react';
import type { StatelessComponent } from 'react';
import { Link } from 'react-router-dom';

import { getCountForTables } from 'utils/CountUtils';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import { createNewCostCenter } from 'permissions/costCenter';

import createReportView from '../components/ReportView/ReportViewBase';
import type { ViewConfig, CellComponentProps } from '../components/ReportView/types';
import NavigationService from 'services/NavigationService';

const IdLink: StatelessComponent<CellComponentProps> = ({ display, rowData }) => {
  return <Link to={`/cost-centers/${rowData.id}`}>{display}</Link>;
};

export default createReportView({
  title: 'Cost Centers',
  userFilterNamespace: 'Cost Centers',
  codename: 'cost-centres',
  type: 'SINGLE',
  view: {
    dataPathname: 'cost-centres',
    apiType: 'SQL',
    columns: [
      {
        columnName: 'code',
        title: 'general:cost_centers.labels.code',
        flexBasis: 200,
        component: IdLink,
      },
      {
        columnName: 'name',
        title: 'general:cost_centers.labels.name',
        flexBasis: 200,
        component: IdLink,
      },
      {
        columnName: 'description',
        title: 'general:cost_centers.labels.description',
        flexBasis: 360,
      },
      {
        columnName: 'projects',
        title: 'general:projects.multiple_assigned',
        flexBasis: 160,
        component: function ProjectsCell({ rowData }) {
          const count = rowData.projects_count;

          if (count === 0) {
            return <span>{count}</span>;
          }

          return (
            <Link
              to={NavigationService.formulateReportViewUri('/projects', {
                page: 1,
                filters: [{ key: 'cost_centre', values: [rowData.id] }],
              })}
            >
              {count}
            </Link>
          );
        },
      },
      {
        columnName: 'assets',
        title: 'general:cost_centers.labels.assets_assigned',
        flexBasis: 160,
        component: function AssetsCell({ display, rowData }) {
          if (getCountForTables(display.all) === 0) {
            return <span>{getCountForTables(display)}</span>;
          }

          return (
            <Link
              to={NavigationService.formulateReportViewUri('/assets', {
                page: 1,
                filters: [
                  { key: 'project', values: [...rowData.projects.map(project => project)] },
                  { key: 'asset_type', values: ['Rented Open', 'Owned'] },
                ],
              })}
            >
              {display.all}
            </Link>
          );
        },
      },
      {
        columnName: 'status',
        title: 'general:cost_centers.labels.status',
        flexBasis: 200,
        component: function StatusCell({ display, rowData }) {
          return (
            <StatusIndicator
              status={rowData.archived_at ? 'Archived' : display ? 'active' : 'inactive'}
            />
          );
        },
      },
    ],
  },
  buttons: [
    {
      label: 'general:actions.new',
      icon: '#icon-add',
      to: '/cost-centers/create',
      permissions: createNewCostCenter,
    },
  ],
});
